import {TFunction} from 'i18next';
import {AnySchema, array, mixed, string, number, date} from 'yup';
import {FieldType} from '../../../shared/model/field.model';
import {getNxFileValidationSchema} from './nx-file-validation-schema';
import {
  getDefaultFieldLengthExceededError,
  getDefaultRequiredError,
  getSchemaBasedOnRequiredField,
  getTooBigError,
  getTooSmallError
} from './validation-schema-utils';
import {getDocumentFieldValidationSchema} from './document-validation-schema';

export const getCustomFieldValueSchema = (partialValidation = false, t: TFunction): AnySchema => {

  const requiredError = getDefaultRequiredError(t);
  const maxLengthSchema = string().max(512, getDefaultFieldLengthExceededError(t, 512));
  const integerMin = -2147483648;
  const integerMax = 2147483647;

  // A cleared numeric input arrives as NaN (or '' before yup's cast); treat it as absent (null).
  // number().nullable() alone is insufficient because yup casts ''/NaN to NaN, which is not null.
  const emptyToNull = (value: number, originalValue: unknown): number | null => {
    if (originalValue === '' || originalValue === null || originalValue === undefined) return null;
    return Number.isNaN(value) ? null : value;
  };

  const integerSchema = number()
    .transform(emptyToNull)
    .nullable()
    .integer(getTooSmallError(t, integerMin))
    .min(integerMin, getTooSmallError(t, integerMin))
    .max(integerMax, getTooBigError(t, integerMax));

  const doubleSchema = number().transform(emptyToNull).nullable();
  const percentageSchema = number()
    .transform(emptyToNull)
    .nullable()
    .min(0, getTooSmallError(t, 0))
    .max(100, getTooBigError(t, 100));
  // Cleared boolean is null; oneOf only exempts undefined (not null), so null must be whitelisted.
  // Empty select ('') is normalized to null. Required branch still rejects null via .required().
  const booleanSchema = string()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .oneOf(['true', 'false', null])
    .nullable();
  const singleDictionarySchema = string().nullable();
  // Cleared date is null, '' or an Invalid Date; normalize all to null first, then allow it.
  const dateSchema = date()
        .transform((value, originalValue) => {
            if (originalValue === '' || originalValue === null || originalValue === undefined) return null;
            return value instanceof Date && isNaN(value.getTime()) ? null : value;
        })
        .nullable()
        .typeError('Invalid date')
        .test('is-valid-date', 'Invalid date', value => {
            if (!value) return true; // allow empty when not required
            return value instanceof Date && !isNaN(value.getTime());
        });

  const getFieldSchema = (required = false): AnySchema =>
    mixed().when('fieldType', fieldType => {
      switch (fieldType) {
        case FieldType.FILE:
          return getNxFileValidationSchema(required, requiredError);
        case FieldType.DOCUMENT:
          return getDocumentFieldValidationSchema(required, requiredError, t);
        case FieldType.DICTIONARY_MULTI:
          return required ? array().min(1, requiredError) : array();
        case FieldType.DICTIONARY_SINGLE:
          return required ? singleDictionarySchema.required(requiredError) : singleDictionarySchema;
        case FieldType.STRING:
          return required ? maxLengthSchema.required(requiredError) : maxLengthSchema;
        case FieldType.INTEGER:
          return required ? integerSchema.required(requiredError) : integerSchema;
        case FieldType.DOUBLE:
        case FieldType.CASH:
          return required ? doubleSchema.required(requiredError) : doubleSchema;
        case FieldType.PERCENTAGE:
          return required ? percentageSchema.required(requiredError) : percentageSchema;
        case FieldType.BOOLEAN:
          return required ? booleanSchema.required(requiredError) : booleanSchema;
        case FieldType.DATE:
          return required ? dateSchema.required(requiredError) : dateSchema;
        default:
          return required ? mixed().required(requiredError) : mixed();
      }
    });

  return getSchemaBasedOnRequiredField(partialValidation, getFieldSchema(), getFieldSchema(true));
};
